import React, { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { listRazorpaySubscriptions, getRazorpaySubscriptionTransactions } from "../api";

const STATES = ["", "INITIATED", "AUTHORIZATION_PENDING", "ACTIVE", "PAUSED", "CANCELLED", "FAILED"];

const STATE_STYLE = {
  ACTIVE:                  { bg: "#dcfce7", color: "#166534" },
  INITIATED:               { bg: "#dbeafe", color: "#1e40af" },
  AUTHORIZATION_PENDING:   { bg: "#dbeafe", color: "#1e40af" },
  PAUSED:                  { bg: "#fef9c3", color: "#854d0e" },
  CANCELLED:               { bg: "#f1f5f9", color: "#475569" },
  FAILED:                  { bg: "#fee2e2", color: "#991b1b" },
};

function Badge({ state }) {
  const style = STATE_STYLE[state] || { bg: "#f1f5f9", color: "#475569" };
  return (
    <span style={{ ...t.badge, backgroundColor: style.bg, color: style.color }}>
      {state}
    </span>
  );
}

function fmt(epochMs) {
  if (!epochMs) return "—";
  return new Date(epochMs).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function fmtAmount(paise) {
  return `₹${(paise / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
}

function fmtDateTime(iso) {
  return new Date(iso).toLocaleString("en-IN");
}

function SubscriptionDetailsModal({ appId, subscription, onClose }) {
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getRazorpaySubscriptionTransactions(appId, subscription.id)
      .then(setTxns)
      .finally(() => setLoading(false));
  }, [appId, subscription.id]);

  return (
    <div style={m.overlay} onClick={onClose}>
      <div style={m.box} onClick={(e) => e.stopPropagation()}>
        <div style={m.header}>
          <h2 style={m.title}>Subscription Details</h2>
          <button onClick={onClose} style={m.closeBtn}>✕</button>
        </div>

        <div style={m.grid}>
          <div style={m.field}><span style={m.label}>Subscription ID</span><code style={m.value}>{subscription.id}</code></div>
          <div style={m.field}><span style={m.label}>State</span><span><Badge state={subscription.state} /></span></div>
          <div style={m.field}><span style={m.label}>Name</span><span style={m.value}>{subscription.name}</span></div>
          <div style={m.field}><span style={m.label}>Email</span><span style={m.value}>{subscription.email || "—"}</span></div>
          <div style={m.field}><span style={m.label}>Phone Number</span><span style={m.value}>{subscription.phone_number}</span></div>
          <div style={m.field}><span style={m.label}>Amount</span><span style={m.value}>{fmtAmount(subscription.amount)}</span></div>
          <div style={m.field}><span style={m.label}>Max Amount</span><span style={m.value}>{subscription.max_amount ? fmtAmount(subscription.max_amount) : "—"}</span></div>
          <div style={m.field}><span style={m.label}>Amount Type</span><span style={m.value}>{subscription.amount_type}</span></div>
          <div style={m.field}><span style={m.label}>Frequency</span><span style={m.value}>{subscription.frequency}</span></div>
          <div style={m.field}><span style={m.label}>Method</span><span style={m.value}>{subscription.method}</span></div>
          <div style={m.field}><span style={m.label}>Razorpay Customer ID</span><code style={m.value}>{subscription.razorpay_customer_id || "—"}</code></div>
          <div style={m.field}><span style={m.label}>Razorpay Order ID</span><code style={m.value}>{subscription.razorpay_order_id || "—"}</code></div>
          <div style={m.field}><span style={m.label}>Razorpay Token ID</span><code style={m.value}>{subscription.razorpay_token_id || "—"}</code></div>
          <div style={m.field}><span style={m.label}>Next Payment</span><span style={m.value}>{fmt(subscription.next_payment_date)}</span></div>
          <div style={m.field}><span style={m.label}>Last Payment Status</span><span style={m.value}>{subscription.last_payment_status || "—"}</span></div>
          <div style={m.field}><span style={m.label}>Total Payments</span><span style={m.value}>{subscription.total_payments}</span></div>
          <div style={m.field}><span style={m.label}>Created</span><span style={m.value}>{fmtDateTime(subscription.created_at)}</span></div>
        </div>

        <h3 style={m.subheading}>Transactions</h3>
        {loading ? (
          <div style={s.loading}>Loading...</div>
        ) : txns.length === 0 ? (
          <div style={s.empty}>No transactions yet.</div>
        ) : (
          <div style={t.wrapper}>
            <table style={t.table}>
              <thead>
                <tr>
                  {["ID", "Amount", "State", "Razorpay Order ID", "Razorpay Payment ID", "Created"].map((h) => (
                    <th key={h} style={t.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {txns.map((tx, i) => (
                  <tr key={tx.id} style={i % 2 === 0 ? t.rowEven : t.rowOdd}>
                    <td style={t.td}><code>{tx.id}</code></td>
                    <td style={t.td}>{fmtAmount(tx.amount)}</td>
                    <td style={t.td}><Badge state={tx.state} /></td>
                    <td style={t.td}><code>{tx.razorpay_order_id || "—"}</code></td>
                    <td style={t.td}><code>{tx.razorpay_payment_id || "—"}</code></td>
                    <td style={t.td}>{fmtDateTime(tx.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default function RazorpaySubscriptions() {
  const { app } = useOutletContext();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stateFilter, setStateFilter] = useState("");
  const [phoneFilter, setPhoneFilter] = useState("");
  const [phoneInput, setPhoneInput] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    listRazorpaySubscriptions(app.id, {
      state: stateFilter || null,
      phone_number: phoneFilter || null,
    })
      .then(setRows)
      .finally(() => setLoading(false));
  }, [app.id, stateFilter, phoneFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={s.wrap}>
      <h1 style={s.h1}>Subscriptions</h1>
      <p style={s.sub}>{app.name} · Razorpay</p>

      {/* Filters */}
      <div style={s.filters}>
        <select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)} style={s.select}>
          {STATES.map((st) => (
            <option key={st} value={st}>{st || "All states"}</option>
          ))}
        </select>
        <div style={s.phoneSearch}>
          <input
            value={phoneInput}
            onChange={(e) => setPhoneInput(e.target.value)}
            placeholder="Search by phone"
            style={s.searchInput}
            onKeyDown={(e) => e.key === "Enter" && setPhoneFilter(phoneInput)}
          />
          <button onClick={() => setPhoneFilter(phoneInput)} style={s.searchBtn}>Search</button>
          {phoneFilter && (
            <button onClick={() => { setPhoneFilter(""); setPhoneInput(""); }} style={s.clearBtn}>✕</button>
          )}
        </div>
        <span style={s.count}>{loading ? "…" : `${rows.length} results`}</span>
      </div>

      {/* Table */}
      {loading ? (
        <div style={s.loading}>Loading...</div>
      ) : rows.length === 0 ? (
        <div style={s.empty}>No subscriptions found.</div>
      ) : (
        <div style={t.wrapper}>
          <table style={t.table}>
            <thead>
              <tr>
                {["Subscription ID", "Customer ID", "Name", "Phone", "Amount", "Method", "Frequency", "State", "Next Payment", "Total Paid", "Created"].map((h) => (
                  <th key={h} style={t.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={row.id} style={i % 2 === 0 ? t.rowEven : t.rowOdd}>
                  <td style={t.td}>
                    <button onClick={() => setSelected(row)} style={t.idBtn}>{row.id}</button>
                  </td>
                  <td style={t.td}><code>{row.razorpay_customer_id || "—"}</code></td>
                  <td style={t.td}>{row.name}</td>
                  <td style={t.td}>{row.phone_number}</td>
                  <td style={t.td}>{fmtAmount(row.amount)}</td>
                  <td style={t.td}>{row.method}</td>
                  <td style={t.td}>{row.frequency}</td>
                  <td style={t.td}><Badge state={row.state} /></td>
                  <td style={t.td}>{fmt(row.next_payment_date)}</td>
                  <td style={t.td}>{row.total_payments}</td>
                  <td style={t.td}>{new Date(row.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <SubscriptionDetailsModal appId={app.id} subscription={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

const s = {
  wrap: {},
  h1: { margin: "0 0 4px", fontSize: 22, fontWeight: 700, color: "#1e293b" },
  sub: { margin: "0 0 24px", fontSize: 14, color: "#64748b" },
  filters: { display: "flex", alignItems: "center", gap: 10, marginBottom: 16, flexWrap: "wrap" },
  select: { padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, backgroundColor: "#fff" },
  phoneSearch: { display: "flex", gap: 6 },
  searchInput: { padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, width: 180 },
  searchBtn: { padding: "8px 14px", backgroundColor: "#4f46e5", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13 },
  clearBtn: { padding: "8px 10px", backgroundColor: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 8, cursor: "pointer", fontSize: 13, color: "#64748b" },
  count: { marginLeft: "auto", fontSize: 13, color: "#94a3b8" },
  loading: { color: "#64748b", padding: 20 },
  empty: { textAlign: "center", padding: "60px 0", color: "#64748b", fontSize: 14 },
};

const t = {
  wrapper: { overflowX: "auto", borderRadius: 10, boxShadow: "0 1px 3px rgba(0,0,0,0.07)" },
  table: { width: "100%", borderCollapse: "collapse", backgroundColor: "#fff", fontSize: 13 },
  th: { padding: "10px 14px", textAlign: "left", backgroundColor: "#f8fafc", color: "#64748b", fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" },
  td: { padding: "11px 14px", color: "#1e293b", borderBottom: "1px solid #f8fafc", verticalAlign: "middle" },
  rowEven: {},
  rowOdd: { backgroundColor: "#fafafa" },
  badge: { display: "inline-block", padding: "2px 8px", borderRadius: 20, fontSize: 11, fontWeight: 600 },
  idBtn: { background: "none", border: "none", padding: 0, color: "#4f46e5", cursor: "pointer", fontSize: 13, fontFamily: "monospace", textDecoration: "underline" },
};

const m = {
  overlay: { position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 },
  box: { backgroundColor: "#fff", borderRadius: 12, padding: 28, width: "100%", maxWidth: 720, maxHeight: "90vh", overflowY: "auto" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 },
  title: { margin: 0, fontSize: 18, color: "#1e293b" },
  closeBtn: { background: "none", border: "none", fontSize: 16, cursor: "pointer", color: "#64748b" },
  grid: { display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 14, marginBottom: 24 },
  field: { display: "flex", flexDirection: "column", gap: 4 },
  label: { fontSize: 11, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.04em" },
  value: { fontSize: 13, color: "#1e293b", wordBreak: "break-all" },
  subheading: { fontSize: 14, fontWeight: 600, color: "#1e293b", margin: "0 0 12px" },
};
